import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { AsYouType } from 'libphonenumber-js';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';

import { StyledFormControl } from '../styled/Inputs';
import { DeleteIcon, DropDownClickable, FieldCol } from '../styled/ContactsForm';
import { IUPhoneLabel, PhoneLabels, PhoneObject } from './PhoneFields';
import { useContactsContext } from '../../contexts/ContactsContext';
import trash from '../../core/assets/trash.png';

import { TAppContact } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
  phone: PhoneObject;
  hideLabel?: boolean;
  autocomplete?: boolean;
  handleDelete: () => void;
  handleChange: (value: string) => void;
  handleLabelChange: (phone: PhoneObject, previousLabel: IUPhoneLabel) => void;
  id?: string;
}

export default function PhoneField({
  contact,
  phone,
  hideLabel = false,
  autocomplete = false,
  handleDelete,
  handleChange,
  handleLabelChange,
  id
}: Props) {
  const ref = useRef<HTMLInputElement>();
  const contactsContext = useContactsContext();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(
    phone.phone.displayNumber ?? phone.phone.phoneNumber ?? '');
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [label, setLabel] = useState<IUPhoneLabel>(phone.label ?? IUPhoneLabel.OtherPhone);

  const isSelfContact = contact.contactId !== undefined && contact.contactId === contactsContext.selfContact?.contactId;
  const isSelfPrimary = label === IUPhoneLabel.PrimaryMobile && isSelfContact;

  useEffect(() => {
    setLabel(phone.label);
  }, [phone.label]);

  useEffect(() => {
    if (ref.current && cursorPos !== null) {
      let seenDigits = 0;
      const value = ref.current.value;
      for (let i = 0; i < value.length; i++) {
        if (seenDigits === cursorPos) {
          ref.current.setSelectionRange(i, i);
          setCursorPos(null);
          break;
        }
        if (/[0-9]/.test(value[i])) {
          seenDigits++;
        }
      }
    }
  }, [cursorPos]);

  const formatPhone = (event: ChangeEvent<HTMLInputElement>) => {
    let previousValue = event.target.value;

    // Find where in the digits the cursor is
    const rawCursorPos = event.target.selectionStart ?? 0;
    const numberOfDigitsBeforeCursor = previousValue.substring(0, rawCursorPos).replace(/[^0-9]/g,'').length;

    previousValue = previousValue.replace(/[^ 0-9()+-]/g, '');
    let value = new AsYouType('US').input(previousValue);
    if (value.length == previousValue.length + 1 && value == phoneNumber) {
      // clicked backspace on a special character. Remove one extra.
      value = value.substring(0, value.length - 2);
    }

    // Prevent numbers with more than 15 digits
    const numbers = value.replace(/[^0-9]/g, '').length;
    if (numbers > 15) {
      return;
    }

    setPhoneNumber(value);
    setCursorPos(numberOfDigitsBeforeCursor);

    handleChange(value);
  };

  const onLabelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newLabel = +e.target.value as IUPhoneLabel;

    setLabel(newLabel);

    handleLabelChange({
      phone: phone.phone,
      label: newLabel
    }, label);
  };

  const options = Object.keys(IUPhoneLabel)
    .filter((key) => {
      const labelOption = Number(key) as IUPhoneLabel;

      if (isNaN(labelOption)) {
        return false;
      }
      if (labelOption === IUPhoneLabel.PrimaryMobile && label !== IUPhoneLabel.PrimaryMobile && isSelfContact) {
        return false;
      }

      return true;
    })
    .map((labelStr) => {
      const label = +labelStr as IUPhoneLabel;
      return <option value={label} key={label}>{PhoneLabels[label]}</option>;
    });

  return (
    <Form.Group as={Row} className="gx-2">
      <FieldCol xs={hideLabel ? 12 : 6}>
        <StyledFormControl
          ref={ref}
          autoComplete={autocomplete && (label === IUPhoneLabel.PrimaryMobile || hideLabel) ? 'tel' : 'do-not-autocomplete'}
          type="tel"
          name="phone"
          disabled={phone.disabled}
          id={id}
          placeholder={hideLabel ? 'Phone' : PhoneLabels[label]}
          value={phoneNumber}
          onChange={formatPhone}/>
      </FieldCol>
      {
        hideLabel ? null :
          <>
            <FieldCol xs={5}>
              <DropDownClickable
                value={label}
                onChange={onLabelChange}
                disabled={phone.disabled}
              >
                {options}
              </DropDownClickable>
            </FieldCol>
            <FieldCol xs={1}>
              {isSelfPrimary ? null : <DeleteIcon src={trash} onClick={handleDelete}/>}
            </FieldCol>
          </>
      }
    </Form.Group>
  );
}
